import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { carPath } from "@/lib/carUrl";
import { Eye, Gauge, Calendar, Fuel, Check, Heart, TrendingDown, RotateCw, TrendingUp } from "lucide-react";
import { ListingSellerPhone } from "@/components/ListingSellerPhone";
import { isAddonActive } from "@/lib/carBoost";
import { getListBoostAiLabels } from "@/lib/carAiLabels";
import { formatPrice, formatKm } from "@/lib/format";
import { useFavorites } from "@/context/FavoritesContext";
import { toast } from "sonner";
import { MiniPreview360 } from "@/components/MiniPreview360";
import { has360Viewer } from "@/lib/viewer360Frames";
import { proxied } from "@/lib/imageProxy";
import { TestListingBadge } from "@/components/TestListingBadge";
import { AiLabelsMarquee } from "@/components/AiLabelsMarquee";
const FALLBACK_IMG = "https://images.unsplash.com/photo-1494976388531-d1058494cdd8?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

export const CarCard = ({ car, onQuickView, onOpenDetail, selected, onToggleCompare, priceDrop, compact = false }) => {
  const navigate = useNavigate();
  const { isFavorite, toggleFavorite, getFavoriteCount } = useFavorites();

  const openDetail = (e) => {
    e?.stopPropagation?.();
    if (onOpenDetail) onOpenDetail(car);
    else navigate(carPath(car));
  };

  const openQuickView = (e) => {
    if (onQuickView) {
      onQuickView(car);
      return;
    }
    openDetail(e);
  };

  const onCardActivate = (e) => {
    openQuickView(e);
  };

  const onCardKeyDown = (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      openQuickView(e);
    }
  };
  const fav = isFavorite(car.id);
  const favCount = getFavoriteCount(car.id, car.favorite_count);
  const [hover, setHover] = useState(false);
  const [imgSrc, setImgSrc] = useState(car.image || FALLBACK_IMG);

  useEffect(() => {
    setImgSrc(car.image || FALLBACK_IMG);
  }, [car.id, car.image]);

  const onFav = async (e) => {
    e.stopPropagation();
    const res = await toggleFavorite(car);
    if (res == null) return;
    toast.success(res.favorited ? "Elmentve a kedvencek közé" : "Eltávolítva a kedvencek közül");
  };

  const has360 = has360Viewer(car);
  const isPriority = isAddonActive(car, "priority_ranking");
  const isBoosted = isAddonActive(car, "list_boost");
  const aiLabels = isBoosted ? getListBoostAiLabels(car) : [];
  const hasAiMarquee = aiLabels.length > 0;
  const isTestListing = Boolean(car.is_test_listing);

  return (
    <div
      data-testid={`car-card-wrap-${car.id}`}
      role="button"
      tabIndex={0}
      onClick={onCardActivate}
      onKeyDown={onCardKeyDown}
      className={`flex flex-col transition-all duration-300 hover:-translate-y-1 cursor-pointer outline-none focus-visible:ring-2 focus-visible:ring-electric/60 rounded-2xl ${
        isBoosted ? "p-0.5 list-boost-pulse" : ""
      }`}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      <div
        data-testid={`car-card-${car.id}`}
        data-list-boosted={isBoosted ? "true" : undefined}
        className={`group glass flex flex-col rounded-2xl overflow-hidden ${
          isBoosted
            ? ""
            : isPriority
              ? "border border-amber-400/40 shadow-lg shadow-amber-500/10 hover:border-amber-300/60"
              : "hover:border-white/20"
        }`}
      >
      <div className="relative overflow-hidden aspect-[16/10] bg-anthracite-800 rounded-t-2xl">
        <img
          src={proxied(imgSrc)}
          alt={`${car.make} ${car.model}`}
          loading="lazy"
          referrerPolicy="no-referrer"
          onError={() => { if (imgSrc !== FALLBACK_IMG) setImgSrc(FALLBACK_IMG); }}
          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
        />

        {/* Hover 360 mini preview (4-frame quick rotate) */}
        <MiniPreview360 car={car} active={hover} />

        <div className="absolute inset-0 bg-gradient-to-t from-anthracite-900/80 via-transparent to-transparent pointer-events-none" />

        <div className="absolute top-3 left-3 z-10 flex flex-col gap-1.5 items-start max-w-[85%]">
          {isTestListing && <TestListingBadge compact />}
          <div className="px-3 py-1 rounded-full glass-strong text-xs tracking-wide text-slate-200">
            {car.year}
          </div>
        </div>

        {has360 && (
          <div
            data-testid={`badge-360-${car.id}`}
            title="360° körbeforgatható"
            className="absolute top-3 right-1/2 translate-x-1/2 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-electric/90 backdrop-blur-md text-white text-[10px] font-semibold tracking-[0.15em] uppercase shadow-lg shadow-electric/30"
          >
            <RotateCw className="w-3 h-3 animate-spin" strokeWidth={2} style={{ animationDuration: "3s" }} />
            360°
          </div>
        )}

        {(isPriority || hasAiMarquee) && (
          <div className="absolute bottom-3 right-3 left-3 sm:left-auto flex flex-col items-end gap-1.5 z-10 pointer-events-none">
            {isPriority && (
              <div
                data-testid={`badge-priority-${car.id}`}
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-amber-500/95 text-anthracite-900 text-[10px] font-bold tracking-wide uppercase shadow-lg"
              >
                <TrendingUp className="w-3 h-3" strokeWidth={2.5} />
                TOP
              </div>
            )}
            {hasAiMarquee && (
              <div className="pointer-events-auto w-full sm:w-auto flex justify-end">
                <AiLabelsMarquee labels={aiLabels} carId={car.id} />
              </div>
            )}
          </div>
        )}

        {priceDrop && (
          <div
            data-testid={`price-drop-badge-${car.id}`}
            className="absolute bottom-3 left-3 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-500/90 text-white text-xs font-medium"
          >
            <TrendingDown className="w-3.5 h-3.5" strokeWidth={2} /> Árcsökkenés
          </div>
        )}

        <div className="absolute top-3 right-3 z-10 flex items-center gap-2">
          {favCount > 0 ? (
            <div className="inline-flex items-center gap-1.5 rounded-full glass-strong pl-2.5 pr-1 py-1">
              <span
                data-testid={`favorite-count-${car.id}`}
                title={`${favCount} kedvenc`}
                className="text-xs font-semibold tabular-nums text-red-300 min-w-[1ch]"
              >
                {favCount}
              </span>
              <button
                data-testid={`favorite-btn-${car.id}`}
                onClick={onFav}
                title={fav ? "Eltávolítás a kedvencekből" : "Mentés a kedvencekhez"}
                className={`w-8 h-8 rounded-full flex items-center justify-center transition-all ${
                  fav ? "text-red-400" : "text-slate-300 hover:text-white"
                }`}
              >
                <Heart className="w-4 h-4" fill={fav ? "currentColor" : "none"} strokeWidth={1.5} />
              </button>
            </div>
          ) : (
            <button
              data-testid={`favorite-btn-${car.id}`}
              onClick={onFav}
              title={fav ? "Eltávolítás a kedvencekből" : "Mentés a kedvencekhez"}
              className={`w-8 h-8 rounded-full glass-strong flex items-center justify-center transition-all ${
                fav ? "text-red-400" : "text-slate-300 hover:text-white"
              }`}
            >
              <Heart className="w-4 h-4" fill={fav ? "currentColor" : "none"} strokeWidth={1.5} />
            </button>
          )}

          {onToggleCompare && (
            <label className="cursor-pointer" onClick={(e) => e.stopPropagation()}>
              <input
                type="checkbox"
                data-testid={`compare-checkbox-${car.id}`}
                checked={selected}
                onChange={() => onToggleCompare(car)}
                className="peer sr-only"
              />
              <span className="flex items-center gap-1.5 px-3 py-1.5 rounded-full glass-strong text-xs text-slate-300 peer-checked:bg-electric peer-checked:text-white transition-all border border-transparent peer-checked:border-electric-light">
                {selected ? <Check className="w-3.5 h-3.5" /> : null}
                Összehasonlít
              </span>
            </label>
          )}
        </div>

        <div
          aria-hidden
          className="absolute bottom-3 left-1/2 -translate-x-1/2 opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0 transition-all duration-300 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-electric text-white text-sm font-medium z-10 pointer-events-none"
        >
          <Eye className="w-4 h-4" strokeWidth={1.5} />
          Gyors nézet
        </div>
      </div>

      <div className={compact ? "p-2.5 flex flex-col flex-1 min-w-0" : "p-5 flex flex-col flex-1"}>
        <div className={compact ? "text-[10px] tracking-[0.12em] uppercase text-slate-400 truncate" : "text-xs tracking-[0.15em] uppercase text-slate-400"}>
          {car.make}
        </div>
        <h3 className={compact ? "font-heading text-sm font-medium mt-0.5 line-clamp-2 leading-snug" : "font-heading text-xl font-medium mt-1"}>
          {car.model}
        </h3>

        <div className={compact ? "grid grid-cols-1 gap-1 mt-2 text-[10px] text-slate-400" : "grid grid-cols-3 gap-2 mt-4 text-xs text-slate-400"}>
          <div className="flex items-center gap-1 min-w-0">
            <Gauge className="w-3 h-3 text-electric shrink-0" strokeWidth={1.5} />
            <span className="truncate">{car.horsepower} LE</span>
          </div>
          <div className="flex items-center gap-1 min-w-0">
            <Fuel className="w-3 h-3 text-electric shrink-0" strokeWidth={1.5} />
            <span className="truncate">{car.fuel}</span>
          </div>
          <div className="flex items-center gap-1 min-w-0">
            <Calendar className="w-3 h-3 text-electric shrink-0" strokeWidth={1.5} />
            <span className="truncate">{formatKm(car.mileage)}</span>
          </div>
        </div>

        <div className={compact ? "mt-auto pt-2 flex flex-col gap-1" : "mt-auto pt-5 flex items-end justify-between gap-2"}>
          <div className="min-w-0">
            {priceDrop && (
              <div data-testid={`old-price-${car.id}`} className="text-[10px] text-slate-500 line-through">
                {formatPrice(priceDrop.old)}
              </div>
            )}
            <div className={`font-heading font-light ${compact ? "text-base" : "text-2xl"} ${priceDrop ? "text-emerald-400" : "text-white"}`}>
              {formatPrice(car.price)}
            </div>
          </div>
          <div className="flex flex-col items-end gap-1 shrink-0">
            <ListingSellerPhone
              car={car}
              compact
              testid={`card-seller-phone-${car.id}`}
            />
            <button
              type="button"
              data-testid={`details-btn-${car.id}`}
              onClick={openDetail}
              className={compact ? "text-[11px] text-left text-electric hover:text-electric-light transition-colors" : "text-sm text-electric hover:text-electric-light transition-colors"}
            >
              Részletek →
            </button>
          </div>
        </div>
      </div>
      </div>
    </div>
  );
};
